import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Search, FileText, Eye, X, Package, Upload, Plus } from "lucide-react";
import { format } from "date-fns";
import { useDropzone } from "react-dropzone";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API_URL = `${BACKEND_URL}/api`;

const SpecialRatePage = () => {
  const [profiles, setProfiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [newProfileName, setNewProfileName] = useState("");

  // STATE UNTUK MODAL DETAIL & UPLOAD
  const [selectedProfile, setSelectedProfile] = useState(null); 
  const [detailRates, setDetailRates] = useState([]); 
  const [viewMode, setViewMode] = useState("DOC"); 
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isUploadMode, setIsUploadMode] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API_URL}/special-rates/profiles`);
      setProfiles(res.data);
    } catch (error) {
      toast.error("Gagal mengambil data profil Special Rate");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    if (!newProfileName.trim()) return toast.error("Nama profil tidak boleh kosong");
    try {
      await axios.post(`${API_URL}/special-rates/profiles`, { name: newProfileName.toUpperCase() });
      toast.success("Profil Special Rate berhasil dibuat!");
      setNewProfileName("");
      fetchProfiles();
    } catch (error) {
      toast.error("Gagal membuat profil. Pastikan nama belum terpakai.");
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Yakin ingin menghapus profil "${name}" beserta seluruh harganya?`)) return;
    try {
      await axios.delete(`${API_URL}/special-rates/profiles/${id}`);
      toast.success("Profil berhasil dihapus!");
      fetchProfiles();
    } catch (error) {
      toast.error("Gagal menghapus profil.");
    }
  };

  const handleViewDetail = async (profile) => {
    setSelectedProfile(profile);
    setViewMode("DOC"); 
    setIsUploadMode(false);
    setIsLoadingDetail(true);
    try {
      const res = await axios.get(`${API_URL}/special-rates/profiles/${profile.id}/rates`);
      setDetailRates(res.data);
    } catch (error) {
      toast.error("Gagal mengambil detail harga");
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const handleOpenUpload = (profile) => {
    setSelectedProfile(profile);
    setIsUploadMode(true);
    setUploadFile(null);
  };

  const closeDetail = () => {
    setSelectedProfile(null);
    setDetailRates([]);
    setUploadFile(null);
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) setUploadFile(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false,
  });

  const handleUploadSubmit = async () => {
    if (!uploadFile) return toast.error("Pilih file Excel dulu!");
    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", uploadFile);

    try {
      const res = await axios.post(`${API_URL}/special-rates/profiles/${selectedProfile.id}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`Berhasil import ${res.data.imported_items} baris harga!`);
      closeDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal upload file.");
    } finally {
      setIsUploading(false);
    }
  };

  const filteredProfiles = profiles.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displayedRates = detailRates.filter(r => r.item_type === viewMode);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      
      {/* HEADER PAGE & CREATE FORM */}
      <div className="flex flex-col lg:flex-row justify-between gap-6 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <span className="text-yellow-500">★</span> Special Rate Profiles
          </h1>
          <p className="text-gray-500 mt-1">Kelola master harga khusus untuk HK, JP, dan KR.</p>
        </div>
        
        <form onSubmit={handleCreateProfile} className="flex gap-2">
          <input 
            type="text" 
            placeholder="Contoh: SP_COLDR_26" 
            className="w-full md:w-64 px-4 py-2 border rounded-xl focus:ring-2 focus:ring-yellow-500 outline-none uppercase font-semibold"
            value={newProfileName}
            onChange={(e) => setNewProfileName(e.target.value)}
          />
          <button type="submit" className="bg-gray-900 hover:bg-black text-white px-5 py-2 rounded-xl font-bold flex items-center gap-2 transition whitespace-nowrap">
            <Plus size={18}/> Buat Profil
          </button>
        </form>
      </div>

      {/* SEARCH BAR */}
      <div className="relative w-full md:w-72">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        <input 
          type="text" 
          placeholder="Cari nama profil..." 
          className="w-full pl-10 pr-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-yellow-500 outline-none shadow-sm"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* TABLE LIST PROFILE */}
      <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-4 text-sm font-semibold text-gray-700">Nama Profil (Rate Code)</th>
              <th className="px-6 py-4 text-sm font-semibold text-gray-700">Tipe Layanan</th>
              <th className="px-6 py-4 text-sm font-semibold text-gray-700">Tanggal Dibuat</th>
              <th className="px-6 py-4 text-sm font-semibold text-gray-700 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading ? (
              <tr><td colSpan="4" className="px-6 py-8 text-center text-gray-500">Loading...</td></tr>
            ) : filteredProfiles.length === 0 ? (
              <tr><td colSpan="4" className="px-6 py-8 text-center text-gray-500">Tidak ada data profil.</td></tr>
            ) : (
              filteredProfiles.map((profile) => (
                <tr key={profile.id} className="hover:bg-yellow-50/30 transition">
                  <td className="px-6 py-4 font-black text-gray-900 text-lg">{profile.name}</td>
                  <td className="px-6 py-4">
                    <span className="px-3 py-1 text-[11px] font-black rounded-full uppercase tracking-wider border bg-yellow-100 text-yellow-700 border-yellow-200">
                        SPECIAL RATE
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 font-mono">
                    {profile.created_at ? format(new Date(profile.created_at), "dd MMM yyyy") : "-"}
                  </td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <button onClick={() => handleOpenUpload(profile)} className="text-green-600 hover:bg-green-50 px-3 py-2 rounded-lg transition font-bold text-sm inline-flex items-center gap-1 border border-transparent hover:border-green-200">
                      <Upload size={16} /> Upload Harga
                    </button>
                    <button onClick={() => handleViewDetail(profile)} className="text-blue-600 hover:bg-blue-50 p-2 rounded-lg transition" title="Lihat Detail">
                      <Eye size={18} />
                    </button>
                    <button onClick={() => handleDelete(profile.id, profile.name)} className="text-red-600 hover:bg-red-50 p-2 rounded-lg transition" title="Hapus Profil">
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* --- MODAL POPUP DETAIL & UPLOAD --- */}
      {selectedProfile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden">
            
            {/* Modal Header */}
            <div className="p-6 border-b flex justify-between items-center bg-gray-50">
              <div>
                <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-black text-gray-900">{selectedProfile.name}</h2>
                    <span className="px-2 py-0.5 text-xs font-bold rounded-md bg-yellow-100 text-yellow-700">SPECIAL</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {isUploadMode ? "Upload Excel Harga untuk profil ini" : "Detail matriks tarif pengiriman"}
                </p>
              </div>
              <button onClick={closeDetail} className="text-gray-400 hover:text-red-600 p-2 rounded-xl bg-white shadow-sm border hover:bg-red-50 transition">
                <X size={24} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 flex flex-col p-6 overflow-hidden bg-white">
              
              {isUploadMode ? (
                 /* TAMPILAN UPLOAD EXCEL */
                 <div className="flex flex-col items-center justify-center h-full max-w-xl mx-auto w-full space-y-6">
                    <div 
                      {...getRootProps()} 
                      className={`w-full border-4 border-dashed rounded-3xl p-12 text-center cursor-pointer transition-all duration-300 ${
                        isDragActive ? 'border-yellow-500 bg-yellow-50 scale-[1.02]' : 'border-gray-200 bg-gray-50 hover:border-yellow-300 hover:bg-yellow-50/30'
                      }`}
                    >
                      <input {...getInputProps()} />
                      <div className="flex flex-col items-center gap-4">
                        {uploadFile ? (
                          <>
                            <div className="w-20 h-20 rounded-2xl bg-green-100 flex items-center justify-center shadow-inner">
                              <FileText className="w-10 h-10 text-green-600" />
                            </div>
                            <div>
                              <p className="text-lg font-bold text-gray-800">{uploadFile.name}</p>
                              <p className="text-sm text-gray-500 mt-1">{(uploadFile.size / 1024).toFixed(2)} KB</p>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="w-20 h-20 rounded-2xl bg-yellow-100 flex items-center justify-center">
                              <Upload className="w-10 h-10 text-yellow-600" />
                            </div>
                            <div>
                              <p className="text-lg font-bold text-gray-800">Drag & drop Excel Special Rate</p>
                              <p className="text-sm text-gray-500 mt-1">Gunakan format template yang sama (HK | KR JP)</p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <button 
                      onClick={handleUploadSubmit}
                      disabled={!uploadFile || isUploading}
                      className="w-full py-4 rounded-2xl font-bold text-lg bg-gray-900 hover:bg-black text-white shadow-xl transition disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {isUploading ? "Memproses..." : "Simpan Harga ke Database"}
                    </button>
                 </div>
              ) : (
                /* TAMPILAN VIEW DETAIL HARGA */
                <>
                  <div className="flex gap-4 mb-4 border-b pb-4">
                    <button 
                        onClick={() => setViewMode("DOC")}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold transition shadow-sm ${viewMode === "DOC" ? "bg-gray-900 text-white" : "bg-white border text-gray-600 hover:bg-gray-50"}`}
                    >
                        <FileText size={18} /> Document (DOX)
                    </button>
                    <button 
                        onClick={() => setViewMode("PARCEL")}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold transition shadow-sm ${viewMode === "PARCEL" ? "bg-gray-900 text-white" : "bg-white border text-gray-600 hover:bg-gray-50"}`}
                    >
                        <Package size={18} /> Non-Document (SPX)
                    </button>
                  </div>

                  <div className="flex-1 overflow-auto border-2 rounded-xl shadow-inner">
                    {isLoadingDetail ? (
                        <div className="flex flex-col items-center justify-center h-full text-gray-500 space-y-3">
                            <div className="w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
                            <p className="font-bold">Memuat data harga...</p>
                        </div>
                    ) : displayedRates.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-gray-400">
                            <FileText size={48} className="mb-2 opacity-20"/>
                            <p>Belum ada harga di profil ini.</p>
                            <p className="text-sm">Silakan klik Upload Harga di menu sebelumnya.</p>
                        </div>
                    ) : (
                        <table className="w-full text-left text-sm whitespace-nowrap">
                            <thead className="bg-gray-100 sticky top-0 z-10 shadow-sm border-b-2">
                                <tr>
                                    <th className="px-5 py-4 font-black text-gray-800 bg-gray-200 sticky left-0 z-20 border-r-2">Weight (Kg)</th>
                                    <th className="px-5 py-4 font-black text-center text-gray-800 border-r">Hongkong (HK)</th>
                                    <th className="px-5 py-4 font-black text-center text-gray-800 border-r">Korea & Jepang (KR JP)</th>
                                    {displayedRates.some(r => r.th_price > 0) && <th className="px-5 py-4 font-black text-center text-gray-800 border-r">Thailand (TH)</th>}
                                    {displayedRates.some(r => r.cn_price > 0) && <th className="px-5 py-4 font-black text-center text-gray-800">China (CN)</th>}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {displayedRates.map((row, idx) => (
                                    <tr key={idx} className={row.is_multiplier ? "bg-yellow-50 hover:bg-yellow-100" : "hover:bg-gray-50"}>
                                        <td className="px-5 py-3 font-bold bg-white sticky left-0 border-r-2 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] z-10 text-gray-800">
                                            {row.weight} {row.is_multiplier && <span className="text-[10px] text-yellow-700 bg-yellow-200 px-1.5 py-0.5 rounded ml-2">/ Kg</span>}
                                        </td>
                                        <td className="px-5 py-3 text-center border-r font-mono text-gray-700">
                                            Rp {row.hk_price.toLocaleString('id-ID')}
                                        </td>
                                        <td className="px-5 py-3 text-center font-mono text-gray-700">
                                            Rp {row.kr_jp_price.toLocaleString('id-ID')}
                                        </td>
                                        {displayedRates.some(r => r.th_price > 0) && <td className="px-5 py-3 text-center border-r font-mono text-gray-700">Rp {(row.th_price || 0).toLocaleString('id-ID')}</td>}
                                        {displayedRates.some(r => r.cn_price > 0) && <td className="px-5 py-3 text-center font-mono text-gray-700">Rp {(row.cn_price || 0).toLocaleString('id-ID')}</td>}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                  </div>
                  <div className="mt-4 text-xs font-bold text-gray-400 text-right bg-gray-50 p-2 rounded-lg border inline-block ml-auto">
                    * Tanda <span className="text-yellow-700 bg-yellow-100 px-1 rounded mx-1">/ Kg</span> berarti harga per kilogram (Multiplier).
                  </div>
                </>
              )}
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default SpecialRatePage;